import { dokumentListenCache } from "../components/documents/dokumentListenCache";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authClient, BEARER_TOKEN_KEY } from "../utils/auth-client";
import { apiUrl } from "../utils/api";
import { pushService } from "../services/pushService";
import { computeAuthLoading } from "./authGate";
import { setCurrentUserPreferenceKey } from "../utils/userPreferences";
import { clearBlobCache, setBlobCacheAuthContext } from "../hooks/useBlobUrl";

export type AuthTeam = { id: string; name: string; teamRole: string };

type AuthContextType = {
  user: any;
  session: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: string[];
  department: string | null;
  teams: AuthTeam[];
  /** Flat union of user-ids in all of the caller's teams (incl. self).
   *  Populated from `GET /rbac/me/permissions`. Used by the Contact list to
   *  filter rows whose K-Verantwortlicher (BO) is on the caller's team. */
  teammates: string[];
  permissions: string[];
  isAdmin: boolean;
  isGfgl: boolean;
  /** TEMPORÄR: true, wenn der Benutzer auf der app-weiten Lösch-Allowlist des
   *  Backends steht (CLL/SAH). Die Kontaktliste blendet damit den Löschen-Button
   *  auch für nicht-admin/gfgl-Benutzer ein. Quelle ist `GET /rbac/me/permissions`
   *  (Backend-Allowlist = einzige Quelle; Rollback = Array dort leeren). */
  canTempDeleteContacts: boolean;
  /** True when the signed-in user has banned=true on the better-auth user
   *  record — for us, that means "self-provisioned via Microsoft and waiting
   *  for an admin to assign role/team". RequireAuth uses this to route to
   *  /pending-approval instead of /access-denied. */
  isPendingApproval: boolean;
  hasPermission: (permission: string) => boolean;
  reloadPermissions: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, isPending } = authClient.useSession();
  const isAuthenticated = !!data?.session;

  const [teams, setTeams] = useState<AuthTeam[]>([]);
  const [teammates, setTeammates] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [canTempDeleteContacts, setCanTempDeleteContacts] = useState(false);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);
  // User-id, für die die RBAC-Permissions zuletzt aufgelöst wurden (geladen ODER
  // bewusst übersprungen). An die id gekoppelt — NICHT an ein Boolean —, damit beim
  // Session-Resolve nach Reload kein „ready"-Render mit noch leeren Permissions
  // durchsickert und RequireAuth fälschlich nach /access-denied leitet.
  const [resolvedPermUserId, setResolvedPermUserId] = useState<string | null>(null);

  const signOut = async () => {
    // Geschützte Bild-/Dateibytes dürfen nie in die nächste Session wandern.
    clearBlobCache();
    dokumentListenCache.leeren();
    await authClient.signOut();
    localStorage.removeItem(BEARER_TOKEN_KEY);
    // Clear cached personal data so the next user doesn't inherit it.
    localStorage.removeItem("refData_foTeam");
    localStorage.removeItem("refData_foTeam_ts");
    localStorage.removeItem("refData_boTeam");
    localStorage.removeItem("refData_boTeam_ts");
    localStorage.removeItem("refData_appTeams");
    localStorage.removeItem("refData_appTeams_ts");
    localStorage.removeItem("refData_users");
    localStorage.removeItem("refData_users_ts");
  };

  // @ts-ignore - role is a better-auth admin-plugin field
  const roles = data?.user?.role ? [data.user.role as string] : [];
  // @ts-ignore - department is a custom field
  const department = data?.user?.department || null;

  const role = roles[0] || null;
  const isAdmin = role === "admin";
  const isGfgl = role === "gfgl";
  // @ts-ignore - banned is a better-auth admin-plugin field
  const isPendingApproval = !!data?.user?.banned;

  // H1: Preference-Key aus der bereits geladenen Session ableiten und in einen
  // Modul-Cache spiegeln, den getCurrentUserPreferenceKey bevorzugt nutzt — so
  // entfällt pro Preference-Ladung ein eigenes authClient.getSession().
  const userPreferenceKey = useMemo(() => {
    const u: any = data?.user;
    if (!u) return null;
    const username = String(u.email ?? u.username ?? u.id ?? "").trim().toLowerCase();
    if (!username) return null;
    const tenantId = String((data?.session as any)?.activeOrganizationId ?? "").trim().toLowerCase();
    return tenantId ? `${tenantId}:${username}` : username;
  }, [data?.user, data?.session]);
  useEffect(() => {
    setCurrentUserPreferenceKey(userPreferenceKey);
    setBlobCacheAuthContext(userPreferenceKey);
  }, [userPreferenceKey]);

  const reloadPermissions = async () => {
    // id, für die dieser Aufruf auflöst — am Anfang festhalten, damit ein
    // zwischenzeitlicher Session-Wechsel den „resolved"-Marker nicht verfälscht.
    const uid = (data?.user?.id as string | undefined) ?? null;
    if (!isAuthenticated || isPendingApproval) {
      // Pending users would 403 with PENDING_APPROVAL on /rbac/me/permissions
      // (the AuthzGuard blocks every endpoint except /rbac/me/status while
      // banned), so skip the fetch entirely instead of spamming the console.
      setTeams([]);
      setTeammates([]);
      setPermissions([]);
      setCanTempDeleteContacts(false);
      // Für diese User gibt es nichts zu laden → als aufgelöst markieren, damit
      // isLoading nicht hängen bleibt (bei Pending greift ohnehin der
      // isPendingApproval-Kurzschluss in computeAuthLoading).
      setResolvedPermUserId(uid);
      return;
    }
    setIsPermissionsLoading(true);
    try {
      const res = await fetch(apiUrl("rbac/me/permissions"));
      if (res.ok) {
        const json = await res.json();
        setTeams(Array.isArray(json.teams) ? json.teams : []);
        setTeammates(Array.isArray(json.teammates) ? json.teammates : []);
        setPermissions(Array.isArray(json.permissions) ? json.permissions : []);
        setCanTempDeleteContacts(json.canTempDeleteContacts === true);
      } else {
        setTeams([]);
        setTeammates([]);
        setPermissions([]);
        setCanTempDeleteContacts(false);
      }
    } catch (e) {
      console.warn("[AuthContext] Failed to load RBAC permissions", e);
      setTeams([]);
      setTeammates([]);
      setPermissions([]);
      setCanTempDeleteContacts(false);
    } finally {
      setIsPermissionsLoading(false);
      // Permissions für diese User sind aufgelöst (ob erfolgreich oder mit
      // leerem Fallback) — erst jetzt darf isLoading auf false fallen.
      setResolvedPermUserId(uid);
    }
  };

  useEffect(() => {
    reloadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, data?.user?.id]);

  // Nach erfolgreichem Login einmal (pro App-Load) die Benachrichtigungs-Erlaubnis
  // anfragen. Zeigt den Browser-Dialog nur, wenn die Permission noch "default" ist;
  // ist sie erteilt, wird still (re-)abonniert. Non-blocking. In-App-Toasts bleiben
  // davon unberührt (immer sichtbar) — nur OS-/Browser-Push hängt an der Erlaubnis.
  useEffect(() => {
    if (!isAuthenticated || isPendingApproval) return;
    pushService.ensureSubscribed({ interactive: true }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const hasPermission = (permission: string) => {
    if (isAdmin || isGfgl) return true;
    return permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user: data?.user || null,
        session: data?.session || null,
        isAuthenticated,
        isLoading: computeAuthLoading({
          sessionPending: isPending,
          isAuthenticated,
          isPendingApproval,
          currentUserId: (data?.user?.id as string | undefined) ?? null,
          resolvedUserId: resolvedPermUserId,
          permissionsFetching: isPermissionsLoading,
        }),
        roles,
        department,
        teams,
        teammates,
        permissions,
        isAdmin,
        isGfgl,
        canTempDeleteContacts,
        isPendingApproval,
        hasPermission,
        reloadPermissions,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/** Wie {@link useAuth}, wirft aber nicht, wenn kein Provider darüber liegt. */
export const useAuthOptional = () => useContext(AuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
